<template>
    <div>
        <b-form-group label-cols="3" label="Name:" label-for="name">
            <b-input
                v-model="name"
                placeholder="Name (required)"
                v-validate="''"
                name="name"
                class="form-control"
                :class="{
                    'border-danger': formError.includes('name')
                }"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Status:" label-for="status">
            <b-select
                v-if="_hasAccess('patient.profile.update.status')"
                v-model="status"
                class="form-control"
                autofocus
                v-validate="''"
                name="status"
            >
                <option value="1">Active</option>
                <option value="3">Deceased</option>
                <option value="2">Inactive</option>
            </b-select>
            <div v-else>
                <b-input v-if="status === 1" value="Active" plaintext />
                <b-input v-else-if="status === 2" value="Inactive" plaintext />
                <b-input v-else-if="status === 3" value="Deceased" plaintext />
            </div>
        </b-form-group>

        <b-form-group label-cols="3" label="Breed:" label-for="breed">
            <vet-select
                module="patient-breed"
                placeholder="Breed (required)"
                v-model="breed"
                name="breed"
                v-validate="''"
                :getOptionLabel="breedLabel"
                :options="breedOptions"
                :class="{
                    'border-danger': formError.includes('breed')
                }"
                multiple
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Color:" label-for="color">
            <b-input
                v-model="color"
                name="color"
                placeholder="Color (optional)"
                class="form-control"
                v-validate="''"
            />
        </b-form-group>
        <b-alert show variant="danger" v-if="wrongSexSaved">
            <span>
                {{ originalSex ? originalSex.value : "" }} option is not
                available for this breed. Please select the correct sex option
                below.
            </span>
        </b-alert>
        <b-form-group label-cols="3" label="Sex:" label-for="sex">
            <b-select
                v-model="sex"
                class="form-control"
                v-validate="'required'"
                :class="{ 'border-danger': errors.has('sex') }"
                name="sex"
            >
                <option
                    v-for="value in sexOptions"
                    :key="value.id"
                    :value="value.id"
                    >{{ value.label }}</option
                >
            </b-select>
        </b-form-group>

        <b-form-group label-cols="3" label="Age:" label-for="age">
            <div class="age-wrapper">
                <b-input
                    v-model="years"
                    name="years"
                    class="form-control"
                    placeholder="Years"
                    @input="ageChanged('years', years)"
                    maxlength="3"
                    :class="{ 'border-danger': formError.includes('years') }"
                    v-validate="''"
                />
                <span>yrs</span>
                <b-input
                    v-model="months"
                    name="months"
                    class="form-control"
                    placeholder="Months"
                    @input="ageChanged('months', months)"
                    maxlength="4"
                    :class="{ 'border-danger': formError.includes('months') }"
                    v-validate="''"
                />
                <span>mos</span>
                <b-input
                    v-model="weeks"
                    name="weeks"
                    class="form-control"
                    placeholder="Weeks"
                    @input="ageChanged('weeks', weeks)"
                    maxlength="4"
                    :class="{ 'border-danger': formError.includes('weeks') }"
                    v-validate="''"
                />
                <span>wks</span>
            </div>
        </b-form-group>

        <b-form-group label-cols="3" label="Birthdate:" label-for="birthdate">
            <date-picker
                :config="{
                    useCurrent: false
                }"
                v-model="birthdate"
                name="birthdate"
                :placeholder="getDateFormat() + ' (optional)'"
                @dp-change="changedBirthdate"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Chip:" label-for="chip">
            <b-input
                v-model="chip"
                name="chip"
                placeholder="Chip (optional)"
                class="form-control"
                v-validate="''"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Weight:" label-for="weight">
            <b-input :value="getWeightWithDate(patient)" plaintext />
        </b-form-group>

        <b-form-group label-cols="3" label="Next Visit:" label-for="nextVisit">
            <b-input :value="nextVisitLabel(patient.next_visit)" plaintext />
        </b-form-group>

        <b-form-group
            label-cols="3"
            label="Primary Provider:"
            label-for="primary-provider"
        >
            <v-select
                id="primary-provider"
                ref="vselectProvider"
                v-model="primaryProvider"
                name="provider"
                aria-placeholder="true"
                placeholder="Primary Provider"
                :options="providers"
                @input="primaryProviderSelected"
                :clearable="false"
            ></v-select>
        </b-form-group>

        <b-form-group
            label-cols="3"
            v-if="
                isRecordSharing &&
                patientSharedRecords.length &&
                isSelectedProviderSharedClinic
            "
        >
            <div class="d-flex align-items-start">
                <input
                    id="update_all_clinic_primary_provider"
                    type="checkbox"
                    class="mr-1 mt-1"
                    v-model="updateAllClinicPrimaryProvider"
                />
                <label
                    for="update_all_clinic_primary_provider"
                    class="font-weight-normal m-0"
                    >Update primary provider for this patient across all shared
                    clinics
                </label>
            </div>
        </b-form-group>

        <modal-status
            ref="modalStatus"
            @cancel="cancelSave"
            @save="saveModalStatus"
        />
    </div>
</template>

<script>
import axios from "axios";
import { findIndex, intersection, map, includes, sortBy } from "lodash";
import VetSelect from "@/module/commons/vet-select";
import ModalStatus from "./modal-edit-status";
import vSelect from "vue-select";
import StaffService from "@/services/staff";
import PatientService from "@/services/patient";

const trackedSexOptions = [22, 23, 24]; // 22 - Wether, 23 - Buck , 24 - Doe
const defaultSexOptions = [1, 2, 3, 4]; // 1 - Male(Intect), 2 - Male(Neutered) , 3 - Female(Intact), 4 - Female(Spayed)
const matchSexOptions = { 1: 23, 2: 22, 3: 24, 4: 24 };
const emptyProvider = { id: null, label: "No primary provider" };

export default {
    components: {
        VetSelect,
        ModalStatus,
        vSelect
    },
    props: {
        patient: {
            type: Object,
            required: true
        },
        /**
         * isFromSoap
         * @default false indicate as it from patient profile module
         */
        isFromSoap: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        isRecordSharing() {
            return this.patient.isRecordSharing;
        },
        isSelectedProviderSharedClinic() {
            if (!this.providerSharedRecords.length) {
                return false;
            }

            return true;
        }
    },
    data() {
        return {
            name: this.patient.name,
            status: this.patient.status,
            breed: this.patient.patient_breeds || [],
            breedOptions: [],
            color: this.patient.color,
            sex: this.patient.sex || "1",
            originalSex: this.patient.patient_sex,
            years: "",
            months: "",
            weeks: "",
            chip: this.patient.chip || "",
            weight: "",
            birthdate: "",
            isBirtdateHasInitated: false,
            birthdateChange: 0,
            formError: [],
            sexOptions: [],
            species: "",
            wrongSexSaved: false,
            primaryProvider: emptyProvider,
            updateAllClinicPrimaryProvider: false,
            providers: [emptyProvider],
            patientSharedRecords: [],
            providerSharedRecords: []
        };
    },
    watch: {
        name(value) {
            if (value !== "") {
                this.clearError("name");
            }
        },
        breed(value) {
            if (value.length > 0) {
                this.clearError("breed");
            } else {
                this.originalSex = null;
            }
            this.breedSelected(value);
        },
        birthdate() {
            this.birthdateChange = 1;
        },
        sex(value) {
            if (this.wrongSexSaved === true && value) {
                this.wrongSexSaved = false;
                this.originalSex = null;
            }
        },
        fields: {
            handler() {
                this.$store.commit("patient/setDirty", this.is_dirty());
            },
            deep: true
        }
    },
    mounted() {
        this.checkPatientExistInRecordSharingClinic();
        this.getProviders();
        this.setBirthdate();
        this.setAge();
        this.setSpecies(this.patient.species);
        this.breedSelected(this.breed);
        this.setPrimaryProvider();
        this.$validator.reset();
        this.$store.commit("patient/setDirty", false);
    },
    methods: {
        /**
         * checkFormValid
         * @return  Boolean
         * @description To check the validation of form fields using validator and formErrors
         */
        async checkFormValid() {
            let valid = await this.$validator.validateAll();
            if (!valid || !this.isValid()) {
                return false;
            }
            return true;
        },
        /**
         * checkFormValid
         * @return  Boolean
         * @description If the parent component is soap-edit (Soap profile), the component only need to popup the modal-status first and the save patient will be triggered manually from the parent component
         */
        async save() {
            const isFormValid = await this.checkFormValid();

            if (!this.isFromSoap) {
                if (!isFormValid) {
                    this.cancelSave();
                    return;
                }
            }

            let status = Number(this.status);

            if (status !== 1) {
                let type;

                if (status === 2) {
                    type = "INACTIVE";
                } else if (status === 3) {
                    type = "DECEASED";
                }

                this.$refs.modalStatus.open({
                    type: type
                });
                return;
            } else if (this.isFromSoap) {
                this.$emit("edit-status");
            } else {
                this.proceedSave();
            }
        },
        cancelSave() {
            // send signal to 'index.vue'
            this.$emit("edit-saved", {
                success: false
            });

            if (this.isFromSoap) {
                this.$emit("routerview-changeissaving", false);
            }
        },
        saveModalStatus(dataModal = {}) {
            if (this.isFromSoap) {
                this.$emit("edit-status", dataModal);
                return;
            } else {
                this.proceedSave(dataModal);
            }
        },
        async proceedSave(dataModal = {}) {
            let details = {};
            let generalErrorMsg = "Error: Unable to save";

            try {
                let breeds = [];
                let birthdate = this.birthdate;
                let owner = this.patient.primary_owner[0];

                // breeds
                for (const b of this.breed) {
                    breeds.push(b.id);
                }

                // birthdate
                if (birthdate === "") {
                    birthdate = 0;
                }

                details = {
                    name: this.name,
                    client_id: owner ? owner.id : "",
                    status: Number(this.status),
                    breed: breeds.join(),
                    breeds: this.breed,
                    color: this.color,
                    sex: Number(this.sex),
                    year: Number(this.years),
                    month: Number(this.months),
                    week: Number(this.weeks),
                    birthdate: birthdate,
                    birthdateChange: this.birthdateChange,
                    chip: this.chip,
                    status_date: dataModal.date,
                    note: dataModal.notes,
                    reminder: dataModal.reminders,
                    task: dataModal.tasks,
                    species: this.species,
                    primary_provider_id: this.primaryProvider
                        ? this.primaryProvider.id
                        : null,
                    update_all_clinic_primary_provider: this
                        .updateAllClinicPrimaryProvider
                };

                // birthdate flag
                if (details.year > 0 || details.month > 0 || details.week > 0) {
                    details.birthdateChange = 1;
                    this.birthdateChange = 1;
                }

                const res = await axios.put(
                    `${process.env.BARRAMUNDI}patient/${this.patient.id}`,
                    details
                );

                if (
                    res.data.response &&
                    res.data.response.message.code === 200
                ) {
                    // If from soap, the message prompt will be triggered from soap-edit
                    if (!this.isFromSoap) {
                        this._message("Record updated");
                        // update store
                        this.updatePatientDetails(res.data.response.resource);
                        // success
                        this.$emit("edit-saved", {
                            success: true,
                            details: details
                        });
                    } else {
                        return true;
                    }
                } else {
                    throw new Error(generalErrorMsg);
                }
            } catch (error) {
                if (!this.isFromSoap) {
                    this._message(generalErrorMsg, "danger");

                    this.$emit("edit-saved", {
                        success: false,
                        details: details
                    });
                } else {
                    return false;
                }
            }
            this.$store.commit("patient/setDirty", false);
        },
        breedLabel(option) {
            return `${option.name} (${option.species_name})`;
        },
        setAge() {
            if (!this.patient.age) {
                return;
            }

            const info = this.patient.age.split(" ");

            for (let n = 0; n < info.length; n++) {
                let label = info[n];
                let value = info[n - 1] || "";

                switch (label) {
                    case "weeks":
                    case "week":
                        this.weeks = value;
                        break;

                    case "year":
                    case "years":
                        this.years = value;
                        break;

                    case "months":
                    case "month":
                        this.months = value;
                        break;
                }
            }
        },
        setBirthdate() {
            const { birthdate, est_birthdate } = this.patient;
            let result = "";

            if (birthdate) {
                result = this._carbonDateDisplay(birthdate);
            } else if (est_birthdate) {
                result = this._carbonDateDisplay(est_birthdate);
            }
            this.birthdate = result;
        },
        clearError(key) {
            this.formError = this.formError.filter(err => err !== key);
        },
        nextVisitLabel(date) {
            if (date) {
                return this._carbonDateDisplay(date);
            }
            return "Not scheduled";
        },
        getWeightWithDate(patient) {
            let weightDate = "";
            if (patient.weightData && patient.weightData.date) {
                weightDate =
                    " (" +
                    this._carbonDateDisplay(patient.weightData.date) +
                    ")";
            }

            // Use a fallback for patient.weight to handle undefined/null values
            return (patient.weight || "") + weightDate;
        },
        updatePatientDetails(newDetails = {}) {
            // after update details, re dispatch get api patient
            this.$store
                .dispatch("patient/getPatientDetails", {
                    id: this.$route.params.id,
                    encoded: this.$route.query.encoded === "0" ? 0 : 1
                })
                .then(details => {
                    this.$root.$emit("update-patient-details", details);
                });
        },
        breedSelected(value) {
            let self = this;
            let index = self.formError.indexOf("breed");
            if (index !== -1) {
                self.formError.splice(index, 1);
            }

            if (value && value.length < 1) {
                self.breedOptions = [];
                self.species = "";

                return;
            }

            /**
             * Couldnt find logic why we are using this condition,
             * so we are disabling this condition because :
             * currently if internet is slow, this code will cause species to be save as "" .
             * if (self.breedOptions.length > 0) {
             *     return;
             * }
             */

            if (value.length > 0) {
                self.getSex(value[0].species_id);
                self.setSpecies(value[0].species_id);
            }

            let params = { query: "", species: value[0].species_id };
            return this.searchBreedsResource(params);
        },
        async searchBreedsResource(params) {
            let self = this;
            self.breedOptions = [];
            let response = await this.$http.get(
                process.env.PATIENT_SERVICE + "breed-search",
                {
                    params
                }
            );

            if (response) {
                self.breedOptions = response.data.response.resources.sort(
                    (firstItem, secondItem) => firstItem.name - secondItem.name
                );
            } else {
                self._message(response.message.errors, "danger");
            }
            return response;
        },
        async getSex(speciesId) {
            this.wrongSexSaved = false;

            let self = this;
            let findSexIndex = -1;
            let response = await this.$http.get(
                process.env.BARRAMUNDI + "patient/sex/" + speciesId
            );

            if (response) {
                let res = response.data.response;
                if (res && res.message && res.message.code === 200) {
                    self.sexOptions = res.resource.data || [];
                    findSexIndex = findIndex(self.sexOptions, function (
                        options
                    ) {
                        return options.id == self.sex;
                    });

                    //if selected sex is NOT exist new sexOptions list , then only change
                    if (findSexIndex === -1 || self.sex == "") {
                        if (
                            this.checkSexBreedRelevance(
                                self.sex,
                                self.sexOptions
                            )
                        ) {
                            self.sex = matchSexOptions[self.sex];
                        } else {
                            if (self.originalSex) {
                                /* Will only check this for FIRST TIME load from DB,
                                 ** If user first time load is correct
                                 ** and then user reselect other breed,
                                 ** originalSex param will be set to null thus skipping this checking
                                 ** and set the sex to the first option as usual
                                 */
                                self.sex = "";
                                this.wrongSexSaved = true;
                                this.isValid();
                            } else {
                                self.sex = self.sexOptions[0].id;
                            }
                        }
                    }
                }
            }
        },
        checkSexBreedRelevance(currentSex, sexOptions) {
            if (currentSex) {
                let relevantSexOptions = intersection(
                    trackedSexOptions,
                    map(sexOptions, "id")
                );

                return (
                    includes(defaultSexOptions, Number(currentSex)) &&
                    relevantSexOptions.length > 0
                );
            }

            return false;
        },
        isValid() {
            let isValid = true;
            if (this.name === "") {
                this.formError.push("name");
                isValid = false;
            }

            if (this.breed.length < 1) {
                this.formError.push("breed");
                isValid = false;
            }

            if (isNaN(this.years)) {
                this.formError.push("years");
                isValid = false;
            }

            if (isNaN(this.weeks)) {
                this.formError.push("weeks");
                isValid = false;
            }

            if (isNaN(this.months)) {
                this.formError.push("months");
                isValid = false;
            }

            if (this.sex == null || this.sex == "") {
                this.formError.push("sex");
                isValid = false;
            }

            return isValid;
        },
        ageChanged(modelName, value) {
            if (isNaN(value) && value) {
                this.formError.push(modelName);
            } else {
                this.clearError(modelName);
            }
            this.birthdate = "";
        },
        is_dirty() {
            return Object.keys(this.fields).some(key => this.fields[key].dirty);
        },
        changedBirthdate() {
            // update birthdate only 2nd++ change date and if originally has birthdate
            if (this.isBirtdateHasInitated || this.patient.birthdate) {
                this.birthdateChange = 1;
            }
            this.isBirtdateHasInitated = true;
        },
        setSpecies(value) {
            if (value) {
                this.species = value;
                return;
            }
        },
        setPrimaryProvider() {
            const primaryProvider = this.patient.primary_provider;

            this.primaryProvider =
                primaryProvider?.id && primaryProvider.business_actor_isactive
                    ? primaryProvider
                    : emptyProvider;

            this.primaryProviderSelected(this.primaryProvider);
        },
        primaryProviderSelected(provider) {
            if (!this.isRecordSharing) {
                return;
            }

            let self = this;

            if (!provider.id) {
                self.providerSharedRecords = [];
                self.updateAllClinicPrimaryProvider = false;
                return;
            }

            StaffService.getProviderRecordSharingClinics(provider.id, {
                is_provider: true
            }).then(response => {
                self.providerSharedRecords = response.resource;
            });
        },
        checkPatientExistInRecordSharingClinic() {
            if (!this.isRecordSharing) {
                return;
            }

            let self = this;

            PatientService.getPatientRecordSharingClinics(this.patient.id).then(
                response => {
                    self.patientSharedRecords = response.resource;
                }
            );
        },
        getProviders() {
            let self = this;

            StaffService.getProviders().then(response => {
                if (response.resource && response.resource.data) {
                    let providers = response.resource.data.map(provider => {
                        return provider.actor;
                    });

                    self.providers.push(...sortBy(providers, ["label"]));
                }
            });
        }
    }
};
</script>

<style scoped>
.age-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
    align-items: center;
    justify-content: center;
}
.age-wrapper span {
    margin-left: 10px;
}
</style>
